// Verträge rund um den Artikel: Gebinde und Barcodes, Bilder, Lieferanten mit datierten Einkaufspreisen,
// Disposition je Standort.
import { PERMISSION_COSTS_READ, defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { BARCODE_KINDS } from '../lib/gtin'
import { zCentsAmount, zDay, zDays, zExpectedVersion, zOptionalId, zOptionalText, zQtyAmount, zSaved, zUnitRef } from './common'
import { zItemUnitInput } from './items'

// ---- Gebinde und Barcodes -----------------------------------------------------------------------------------

export const itemSetUnits = defineContract({
  name: 'masterdata.item.setUnits',
  title: 'Gebinde und Barcodes speichern',
  description:
    'Speichert die Gebinde eines Artikels mit Umrechnung und Barcodes als Ganzes: vorhandene Zeilen ' +
    'werden geändert, neue angelegt, fehlende entfernt – sofern sie nirgends verwendet werden.',
  llm: {
    description:
      'Ersetzt die Gebindeliste eines Artikels (Stück, Karton, Palette) samt Barcodes. IMMER alle Gebinde senden, ' +
      'vorhandene mit ihrer `id`. Genau eines ist die Basiseinheit (baseQty 1000); ein Karton mit 20 Stück hat ' +
      'baseQty 20000. Die Umrechnung eines verwendeten Gebindes lässt sich nicht mehr ändern – dann ein neues Gebinde ' +
      'anlegen und das alte auf inaktiv setzen. Für einen einzelnen zusätzlichen Barcode genügt masterdata.barcode.add.',
    keywords: ['Gebinde', 'Palette', 'Umrechnung', 'Verpackungseinheit', 'EAN', 'Barcode'],
  },
  input: z.strictObject({
    itemId: zId,
    expectedVersion: zExpectedVersion,
    units: z.array(zItemUnitInput).min(1, { error: 'Ein Artikel braucht mindestens seine Basiseinheit.' }).max(30),
  }),
  output: zSaved,
  permission: 'masterdata.item.write',
  risk: 'write',
  limits: { maxItems: 30, itemCount: (input) => input.units.length },
})

export const barcodeAdd = defineContract({
  name: 'masterdata.barcode.add',
  title: 'Barcode zuordnen',
  description: 'Ordnet einem Gebinde eines Artikels einen weiteren Barcode zu.',
  llm: {
    description:
      'Ordnet einen Barcode (GTIN/EAN oder Hausnummer) einem Gebinde eines Artikels zu. Die Prüfziffer einer GTIN wird ' +
      'geprüft; jeder Code gehört im Betrieb zu genau einem Gebinde. Verwenden, wenn ein gescannter Code unbekannt ist ' +
      'und zu einem vorhandenen Artikel gehört.',
    keywords: ['Barcode', 'EAN', 'GTIN', 'zuordnen', 'unbekannter Code', 'Strichcode'],
  },
  input: z.strictObject({
    /** Gebinde des Artikels (item_unit.id aus masterdata.item.get) */
    itemUnitId: zId,
    code: z.string().trim().min(1, { error: 'Bitte einen Barcode eingeben.' }).max(64),
    kind: z.enum(BARCODE_KINDS).default('gtin'),
    isPrimary: z.boolean().default(false),
  }),
  output: z.object({ id: zId, itemId: zId }),
  permission: 'masterdata.item.write',
  risk: 'write',
  effects: { reversible: true },
})

export const barcodeRemove = defineContract({
  name: 'masterdata.barcode.remove',
  title: 'Barcode entfernen',
  description: 'Löst die Zuordnung eines Barcodes zu seinem Gebinde.',
  llm: {
    description: 'Entfernt einen Barcode von seinem Gebinde (falsch zugeordnet, vom Hersteller neu vergeben).',
    keywords: ['Barcode entfernen', 'EAN löschen', 'falscher Barcode'],
  },
  input: z.strictObject({ id: zId }),
  output: z.object({ itemId: zId }),
  permission: 'masterdata.item.write',
  risk: 'write',
})

const zResolvedUnit = z.object({
  id: zId,
  short: z.string(),
  label: z.string(),
  baseQty: z.int(),
  isBase: z.boolean(),
  decimals: z.int(),
})

export const barcodeResolve = defineContract({
  name: 'masterdata.barcode.resolve',
  title: 'Barcode nachschlagen',
  description: 'Löst einen gescannten oder getippten Code zum Artikel und seinem Gebinde auf.',
  llm: {
    description:
      'Findet zu einem Barcode den Artikel UND das Gebinde, das der Code bezeichnet (das Stück hat eine andere GTIN ' +
      'als der Karton). Kennt EAN-13, UPC-A und GTIN-14 als dieselbe Nummer. `hit` ist null, wenn der Code im Betrieb ' +
      'unbekannt ist. Verwenden bei jedem Scan, bevor gebucht wird.',
    keywords: ['scannen', 'Barcode', 'EAN', 'GTIN', 'Strichcode', 'welcher Artikel'],
  },
  input: z.strictObject({ code: z.string().trim().min(1).max(64) }),
  output: z.object({
    hit: z
      .object({
        /** der hinterlegte Code (kann von der gescannten Schreibweise abweichen: führende Null) */
        code: z.string(),
        itemId: zId,
        sku: z.string(),
        name: z.string(),
        active: z.boolean(),
        unitId: zId,
        units: z.array(zResolvedUnit),
        lotTracked: z.boolean(),
        bestBeforeRequired: z.boolean(),
        taxCodeId: zId,
      })
      .nullable(),
  }),
  permission: 'masterdata.item.read',
  risk: 'read',
  scope: 'any',
})

// ---- Bilder ---------------------------------------------------------------------------------------------------

export const itemSetImages = defineContract({
  name: 'masterdata.item.setImages',
  title: 'Artikelbilder speichern',
  description: 'Legt die Bilder eines Artikels in ihrer Reihenfolge fest; das erste ist das Vorschaubild.',
  llm: {
    description:
      'Setzt die Bildliste eines Artikels (Reihenfolge = Anzeige, erstes Bild = Vorschau). Die Dateien müssen vorher ' +
      'über POST /api/files hochgeladen sein. Nur für die Oberfläche.',
    keywords: ['Bild', 'Foto', 'Artikelbild'],
  },
  input: z.strictObject({
    itemId: zId,
    images: z
      .array(z.strictObject({ fileId: zId, alt: zOptionalText(160) }))
      .max(12, { error: 'Je Artikel sind höchstens 12 Bilder möglich.' }),
  }),
  output: z.object({ itemId: zId, count: z.int() }),
  permission: 'masterdata.item.write',
  risk: 'write',
  assistant: 'never',
})

// ---- Lieferanten und Einkaufspreise ------------------------------------------------------------------------

export const zItemSupplierRow = z.object({
  id: zId,
  itemId: zId,
  itemSku: z.string(),
  itemName: z.string(),
  supplierId: zId,
  supplierNumber: z.string(),
  supplierName: z.string(),
  supplierSku: z.string().nullable(),
  orderUnit: z.object({ id: zId, short: z.string(), label: z.string().nullable(), baseQty: z.int() }),
  /** Einkaufspreis netto in Cent je `pricePerQty` der Bestelleinheit – nur mit dem Recht „Einstandspreise sehen“ */
  priceCents: z.int().optional(),
  pricePerQty: z.int().optional(),
  /** Mindestbestellmenge in Tausendstel der Bestelleinheit */
  minOrderQty: z.int().nullable(),
  leadTimeDays: z.int().nullable(),
  isPreferred: z.boolean(),
  validFrom: z.string(),
  validTo: z.string().nullable(),
  /** am Stichtag gültig (sonst: früherer oder künftiger Preis) */
  current: z.boolean(),
})

export const itemSupplierList = defineContract({
  name: 'masterdata.itemSupplier.list',
  title: 'Lieferanten eines Artikels anzeigen',
  description: 'Lieferantenzuordnungen eines Artikels – oder die Artikel eines Lieferanten – mit Bestelleinheit und Einkaufspreis.',
  llm: {
    description:
      'Listet, welcher Lieferant einen Artikel liefert (bzw. welche Artikel ein Lieferant liefert): Artikelnummer beim ' +
      'Lieferanten, Bestelleinheit, Mindestmenge, Lieferzeit, Hauptlieferant und – nur mit dem Recht „Einstandspreise ' +
      'sehen“ – der datierte Einkaufspreis. `history: true` zeigt auch frühere und künftige Preise.',
    keywords: ['Lieferant', 'Einkaufspreis', 'EK', 'Bestelleinheit', 'wer liefert', 'Bezugsquelle', 'Preishistorie'],
  },
  input: z
    .strictObject({
      itemId: zId.optional(),
      supplierId: zId.optional(),
      /** Stichtag, Standard heute */
      date: zDay.optional(),
      /** auch frühere und künftige Preise */
      history: z.boolean().default(false),
    })
    .refine((input) => Boolean(input.itemId || input.supplierId), {
      path: ['itemId'],
      error: 'Bitte einen Artikel oder einen Lieferanten angeben.',
    }),
  output: z.object({ rows: z.array(zItemSupplierRow) }),
  permission: 'masterdata.item.read',
  risk: 'read',
  scope: 'any',
  fieldPermissions: { 'rows.*.priceCents': PERMISSION_COSTS_READ, 'rows.*.pricePerQty': PERMISSION_COSTS_READ },
})

export const itemSupplierSet = defineContract({
  name: 'masterdata.itemSupplier.set',
  title: 'Lieferant und Einkaufspreis setzen',
  description:
    'Ordnet einem Artikel einen Lieferanten mit Bestelleinheit zu und setzt den Einkaufspreis ab einem Datum. ' +
    'Ein neuer Preis ersetzt den bisherigen nicht – der alte endet am Vortag und bleibt in der Historie.',
  llm: {
    description:
      'Ordnet einen Lieferanten einem Artikel zu oder ändert die Zuordnung. Mit `price` entsteht ein neuer Einkaufspreis ' +
      'ab `validFrom` (Standard heute); der bisherige Preis endet am Tag davor und bleibt erhalten. Ohne `price` ändern ' +
      'sich nur Artikelnummer beim Lieferanten, Mindestmenge, Lieferzeit oder das Kennzeichen Hauptlieferant. ' +
      'Preise in Cent netto je Bestelleinheit. Verkaufspreise gehören ins Modul Preise.',
    keywords: ['Einkaufspreis', 'EK ändern', 'Preiserhöhung', 'Lieferant zuordnen', 'Hauptlieferant', 'Bestelleinheit'],
  },
  input: z.strictObject({
    itemId: zId,
    supplierId: zId,
    /** Gebinde, in dem bestellt wird (item_unit.id) */
    orderUnitId: zId,
    supplierSku: zOptionalText(60),
    minOrderQty: zQtyAmount.nullish(),
    leadTimeDays: zDays.nullish(),
    isPreferred: z.boolean().optional(),
    price: z
      .strictObject({
        priceCents: zCentsAmount,
        /** Preiseinheit in Tausendstel der Bestelleinheit: 1000 = je Gebinde, 100_000 = je 100 */
        pricePerQty: z.int().positive().default(1000),
        validFrom: zDay.optional(),
      })
      .optional(),
  }),
  output: z.object({ id: zId, created: z.boolean() }),
  permission: 'masterdata.item.purchasing',
  risk: 'write',
})

export const itemSupplierEnd = defineContract({
  name: 'masterdata.itemSupplier.end',
  title: 'Lieferantenzuordnung beenden',
  description: 'Beendet die Zuordnung eines Lieferanten zu einem Artikel zu einem Datum. Die Preishistorie bleibt.',
  llm: {
    description:
      'Beendet eine Lieferantenzuordnung (der Lieferant liefert den Artikel nicht mehr). Der laufende Einkaufspreis ' +
      'endet am genannten Tag; künftige Preise der Zuordnung entfallen.',
    keywords: ['Lieferant entfernen', 'liefert nicht mehr', 'Bezugsquelle beenden'],
  },
  input: z.strictObject({ itemId: zId, supplierId: zId, orderUnitId: zId, validTo: zDay.optional() }),
  output: z.object({ ended: z.int() }),
  permission: 'masterdata.item.purchasing',
  risk: 'write',
})

// ---- Disposition je Standort --------------------------------------------------------------------------------

export const zItemLocationRow = z.object({
  locationId: zId,
  code: z.string(),
  name: z.string(),
  kind: z.string(),
  /** false: für den Standort ist noch nichts eingestellt – es gelten die Standardwerte */
  configured: z.boolean(),
  listed: z.boolean(),
  minQty: z.int(),
  reorderQty: z.int(),
  targetQty: z.int(),
  defaultBin: z.object({ id: zId, code: z.string() }).nullable(),
  abcClass: z.string().nullable(),
  onHand: z.int(),
  available: z.int(),
  /** der Nutzer darf die Disposition an diesem Standort ändern */
  editable: z.boolean(),
})

export const itemLocationList = defineContract({
  name: 'masterdata.itemLocation.list',
  title: 'Disposition eines Artikels anzeigen',
  description: 'Listung, Mindest-, Melde- und Zielbestand sowie Standardplatz eines Artikels je Standort.',
  llm: {
    description:
      'Liefert für einen Artikel je Standort: gelistet ja/nein, Mindestbestand, Meldebestand (ab hier wird ' +
      'nachbestellt), Zielbestand (bis hierhin wird aufgefüllt), Standardplatz und den aktuellen Bestand. Mengen in ' +
      'Tausendstel der Basiseinheit.',
    keywords: ['Meldebestand', 'Mindestbestand', 'Zielbestand', 'Disposition', 'gelistet', 'Sortiment der Filiale'],
  },
  input: z.strictObject({ itemId: zId }),
  output: z.object({ itemId: zId, baseUnit: zUnitRef, rows: z.array(zItemLocationRow) }),
  permission: 'masterdata.item.read',
  risk: 'read',
  scope: 'any',
})

export const itemLocationSet = defineContract({
  name: 'masterdata.itemLocation.set',
  title: 'Disposition setzen',
  description:
    'Setzt Listung, Mindest-, Melde- und Zielbestand für einen oder mehrere Artikel an einem oder mehreren Standorten. ' +
    'Nur die genannten Werte ändern sich.',
  llm: {
    description:
      'Setzt die Disposition je Standort – für einen oder viele Artikel und einen oder viele Standorte in einem Schritt. ' +
      'Mengen in Tausendstel der Basiseinheit (400 Stück = 400000). Es gilt Mindestbestand ≤ Meldebestand ≤ ' +
      'Zielbestand. `listed: false` nimmt den Artikel aus dem Sortiment des Standorts. Der Standardplatz lässt sich nur ' +
      'für genau einen Standort setzen. Wirkt auf die Bestellvorschläge.',
    keywords: ['Meldebestand ändern', 'Mindestbestand', 'Zielbestand', 'auslisten', 'listen', 'Nachschub', 'Disposition'],
    examples: [
      {
        itemIds: ['00000000-0000-7000-8000-000000000001'],
        locationIds: ['00000000-0000-7000-8000-000000000002'],
        reorderQty: 400_000,
        targetQty: 1_200_000,
      },
    ],
  },
  input: z.strictObject({
    itemIds: z.array(zId).min(1).max(200),
    locationIds: z.array(zId).min(1).max(50),
    listed: z.boolean().optional(),
    minQty: zQtyAmount.optional(),
    reorderQty: zQtyAmount.optional(),
    targetQty: zQtyAmount.optional(),
    /** nur bei genau einem Standort; null = kein Standardplatz */
    defaultBinId: zOptionalId,
    abcClass: z.enum(['A', 'B', 'C']).nullish(),
  }),
  output: z.object({ changed: z.int(), unchanged: z.int() }),
  permission: 'masterdata.item.planning',
  risk: 'write',
  scope: (input) => input.locationIds,
  effects: { reversible: true },
  limits: { maxItems: 2000, itemCount: (input) => input.itemIds.length * input.locationIds.length },
})
